/**
 * Doctor Teams API (Phase 2.7)
 *
 * Admin-authored team membership for each doctor. Endpoints sit under
 * /api/organisation/doctor-teams/* and are gated server-side by:
 *   - MANAGE_DOCTOR_TEAMS (ADMIN only) — write + audit reads
 *   - VIEW_OWN_TEAM (DOCTOR + ADMIN) — list + get-detail
 *
 *   GET    /doctor-teams                       list (admin: all; doctor: own)
 *   GET    /doctor-teams/{doctor_member_id}    fetch one (doctor: self only)
 *   PUT    /doctor-teams/{doctor_member_id}    create / replace
 *   DELETE /doctor-teams/{doctor_member_id}    delete
 *   GET    /doctor-teams/{doctor_member_id}/audit  audit trail (admin)
 *
 * Every function returns `{ success, data?, error? }`.
 */

import apiClient from './apiClient';


function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const status = error?.response?.status;
  const serverDetail = data?.detail;
  if (serverDetail && typeof serverDetail === 'object') {
    if (serverDetail.message) return serverDetail.message;
    return JSON.stringify(serverDetail);
  }
  if (typeof serverDetail === 'string') return serverDetail;
  if (data?.message) return data.message;

  switch (status) {
    case 400: return 'Invalid request — please check the form.';
    case 401: return 'Session expired. Please log in again.';
    case 403: return 'Only an admin can edit doctor teams.';
    case 404: return 'Team not found.';
    case 409: return 'Conflict — please refresh and try again.';
    case 422: return 'Invalid data — please check the form.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

function ok(response) {
  return { success: true, data: response.data };
}

function fail(error, fallback) {
  console.error('[DoctorTeamAPI]', error);
  return { success: false, error: buildApiError(error, fallback) };
}


// ── CRUD ────────────────────────────────────────────────────────────

/**
 * GET /doctor-teams
 * Admin sees every team in the org; doctor sees only her own row (or
 * nothing if she has no team configured).
 */
export const listTeams = async () => {
  try {
    const r = await apiClient.get('/api/organisation/doctor-teams');
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to load doctor teams.');
  }
};

export const getTeam = async (doctorMemberId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/doctor-teams/${doctorMemberId}`,
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to load team.');
  }
};

/**
 * PUT /doctor-teams/{doctor_member_id}
 *
 * `body` shape: { members: [{member_id, role, label?}, ...], reason?: string }
 *
 * Idempotent: pass the complete member list each time. Service diffs
 * against the existing row to emit a focused audit action
 * (created / member_added / member_removed / member_relabeled).
 *
 * Side effect: removing a member with in-flight pre-execute sittings
 * on that doctor's plans flips those sittings to `needs_reassignment`.
 */
export const upsertTeam = async (doctorMemberId, body) => {
  try {
    const r = await apiClient.put(
      `/api/organisation/doctor-teams/${doctorMemberId}`,
      body,
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to update team.');
  }
};

export const deleteTeam = async (doctorMemberId) => {
  try {
    const r = await apiClient.delete(
      `/api/organisation/doctor-teams/${doctorMemberId}`,
    );
    return { success: true, data: r.data ?? null };
  } catch (err) {
    return fail(err, 'Failed to delete team.');
  }
};

export const listAuditEntries = async (doctorMemberId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/doctor-teams/${doctorMemberId}/audit`,
    );
    return ok(r);
  } catch (err) {
    return fail(err, 'Failed to load team audit trail.');
  }
};


const doctorTeamApi = {
  listTeams,
  getTeam,
  upsertTeam,
  deleteTeam,
  listAuditEntries,
};

export default doctorTeamApi;
